import {
  AlertTriangle,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  CircleHelp,
  Clock3,
  PackageCheck,
  RefreshCw,
  RotateCcw,
  TrendingUp,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { orderAnalyticsService } from '../services/api';
import './css/OrderAnalytics.css';

const numberFormatter = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 2 });
const integerFormatter = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 0 });

const formatNumber = value => numberFormatter.format(Number(value || 0));
const formatInteger = value => integerFormatter.format(Number(value || 0));
const formatDate = value => {
  if (!value) return '—';
  const [year, month, day] = value.split('-');
  return year && month && day ? `${day}.${month}.${year}` : value;
};
const formatMonth = value => {
  if (!value) return value;
  const [year, month] = value.split('-');
  return `${month}.${year}`;
};

const KPI_EXPLANATIONS = {
  positions: 'Liczba unikalnych pozycji zamówień dostawcy w wybranym okresie. Pozycję identyfikuje numer zamówienia, numer pozycji i numer materiału; transze tej samej pozycji nie są liczone podwójnie.',
  onTime: 'Odsetek w pełni dostarczonych pozycji, które mają pierwotny termin i datę pełnej dostawy. Pozycja jest terminowa, gdy pełna zamówiona ilość dotarła najpóźniej w pierwotnym terminie.',
  averageDelay: 'Średnia liczba dni kalendarzowych między pierwotnym terminem a datą pełnej dostawy. Do średniej trafiają wyłącznie zakończone pozycje dostarczone po terminie.',
  maximumDelay: 'Największa liczba dni kalendarzowych opóźnienia wśród zakończonych pozycji z pierwotnym terminem i znaną datą pełnej dostawy.',
  changes: 'Łączna liczba zapisanych zmian terminu, które udało się dopasować do pozycji zamówienia w wybranym okresie. Kilka kolejnych zmian tej samej pozycji jest liczonych osobno.',
  leadTime: 'Średnia liczba dni kalendarzowych od daty potwierdzenia zamówienia do dnia, w którym została dostarczona pełna ilość pozycji. Liczone tylko dla zakończonych pozycji z obiema datami.',
  overdueOpen: 'Otwarte pozycje z ilością pozostałą do dostarczenia większą od zera, których aktualny termin dostawy jest wcześniejszy niż dzisiejsza data.',
  remaining: 'Suma ilości zamówionej pomniejszonej o dostawy trwale zaalokowane do pozycji. Ilość dostarczona dla jednej pozycji nie może przekroczyć ilości zamówionej.',
  orderOtif: 'Odsetek zleceń po pierwotnym terminie, w których dostarczono wszystkie pozycje i ilości, a każda pozycja została skompletowana najpóźniej w swoim pierwotnym terminie. Zlecenia z terminem dzisiaj lub później oraz bez kompletu pierwotnych terminów są poza próbą.',
  overdueOpenOrders: 'Odsetek zleceń po pierwotnym terminie, w których co najmniej jedna pozycja lub część ilości nadal nie została dostarczona. Średni wiek zaległości jest liczony od pierwotnego terminu całego zlecenia.',
  orderCompletionDelay: 'Średnia liczba dni kalendarzowych między pierwotnym terminem całego zlecenia a jego faktycznym skompletowaniem. Statystyki obejmują wyłącznie zakończone zlecenia skompletowane po terminie; P90 oznacza próg osiągany lub przekraczany przez około 10% tych zleceń.',
  orderLeadTime: 'Średnia liczba dni kalendarzowych od daty zamówienia do dnia dostarczenia ostatniej brakującej pozycji lub ilości. Obejmuje kompletne zlecenia ze znanymi datami dostaw.',
  changedOrders: 'Odsetek zleceń, w których co najmniej jedna pozycja miała zapisaną zmianę terminu. Nie każda taka zmiana wydłuża termin skompletowania całego zlecenia.',
  extendedOrders: 'Odsetek zleceń, dla których najpóźniejszy aktualny termin którejkolwiek pozycji jest późniejszy od najpóźniejszego pierwotnego terminu całego zlecenia.',
  averageOrderExtension: 'Średnia liczba dni kalendarzowych między pierwotnym a aktualnym terminem skompletowania. Średnia obejmuje wyłącznie zlecenia, których termin rzeczywiście się wydłużył.',
  maximumOrderExtension: 'Największe przesunięcie terminu skompletowania jednego zlecenia. Termin zlecenia wyznacza materiał z najpóźniejszym aktualnym terminem dostawy.',
};

const KpiCard = ({ icon: Icon, label, value, detail, explanation, tone = 'neutral' }) => (
  <article
    className={`oa-kpi oa-kpi--${tone}`}
    tabIndex={explanation ? 0 : undefined}
    aria-label={explanation ? `${label}: ${value}. ${detail || ''} Sposób obliczania: ${explanation}` : undefined}
  >
    <span className="oa-kpi__icon"><Icon size={21} aria-hidden="true" /></span>
    <div>
      <span className="oa-kpi__label">{label}</span>
      <strong className="oa-kpi__value">{value}</strong>
      {detail && <span className="oa-kpi__detail">{detail}</span>}
    </div>
    {explanation && (
      <>
        <CircleHelp className="oa-kpi__help" size={16} aria-hidden="true" />
        <span className="oa-kpi__tooltip" role="tooltip">{explanation}</span>
      </>
    )}
  </article>
);

const AnalyticsTable = ({ title, rows, mode }) => (
  <section className="oa-panel oa-table-panel">
    <div className="oa-panel__header">
      <div>
        <span className="oa-eyebrow">Pozycje wymagające uwagi</span>
        <h3>{title}</h3>
      </div>
      <span className="oa-count">{rows.length}</span>
    </div>
    {rows.length === 0 ? (
      <div className="oa-empty">Brak danych dla wybranego zakresu.</div>
    ) : (
      <div className="oa-table-scroll">
        <table className="oa-table oa-position-table">
          <thead>
            <tr>
              <th>Zamówienie</th>
              <th>Poz.</th>
              <th>Materiał</th>
              <th>Nazwa</th>
              <th>Data zam.</th>
              <th>Pierwotny termin</th>
              <th>{mode === 'delay' ? 'Pełna dostawa' : 'Aktualny termin'}</th>
              <th>{mode === 'delay' ? 'Opóźnienie' : 'Zmiany'}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={`${mode}:${row.order_number}:${row.position}:${row.material_number}`}>
                <td>{row.order_number}</td>
                <td>{row.position}</td>
                <td><strong>{row.material_number}</strong></td>
                <td>{row.description}</td>
                <td>{formatDate(row.order_date)}</td>
                <td>{formatDate(row.initial_due_date)}</td>
                <td>{formatDate(mode === 'delay' ? row.completion_date : row.current_due_date)}</td>
                <td>
                  <strong className={mode === 'delay' ? 'oa-negative' : ''}>
                    {mode === 'delay' ? `${row.delay_days} dni` : row.change_count}
                  </strong>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </section>
);

const OrderExtensionTable = ({ rows }) => (
  <section className="oa-panel oa-table-panel">
    <div className="oa-panel__header">
      <div>
        <span className="oa-eyebrow">Wpływ pojedynczych materiałów</span>
        <h3>Zlecenia z największym wydłużeniem terminu</h3>
      </div>
      <span className="oa-count">{rows.length}</span>
    </div>
    {rows.length === 0 ? (
      <div className="oa-empty">Brak zleceń z wydłużonym terminem w wybranym zakresie.</div>
    ) : (
      <div className="oa-table-scroll">
        <table className="oa-table oa-order-extension-table">
          <thead><tr><th>Zlecenie</th><th>Zam. klienta</th><th>Pierwotne skompletowanie</th><th>Aktualne skompletowanie</th><th>Wydłużenie</th><th>Materiały blokujące</th><th>Stan</th></tr></thead>
          <tbody>{rows.map(row => (
            <tr key={row.order_number}>
              <td><strong>{row.order_number}</strong></td>
              <td>{row.customer_order_designations.join(', ') || '—'}</td>
              <td>{formatDate(row.initial_deadline)}</td>
              <td>{formatDate(row.current_deadline)}</td>
              <td><strong className="oa-negative">{formatInteger(row.extension_days)} dni</strong></td>
              <td><div className="oa-material-list">{row.bottleneck_materials.map(material => (
                <span key={`${material.position}:${material.material_number}:${material.description}`}><strong>{material.material_number}</strong> — {material.description}<small>poz. {material.position} · termin {formatDate(material.current_due_date)}</small></span>
              ))}</div></td>
              <td>{formatInteger(row.open_positions)} otw. · {formatNumber(row.remaining_quantity)} szt.</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    )}
  </section>
);

export const SupplierOrderAnalytics = ({ supplier = 'winkhaus' }) => {
  const isSchuco = supplier === 'schuco';
  const supplierLabel = isSchuco ? 'Schüco' : 'Winkhaus';
  const [analytics, setAnalytics] = useState(null);
  const [filters, setFilters] = useState({ startDate: '', endDate: '', department: '' });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = isSchuco
        ? await orderAnalyticsService.getSchucoAnalytics(appliedFilters)
        : await orderAnalyticsService.getWinkhausAnalytics(appliedFilters);
      setAnalytics(result);
    } catch (err) {
      setError(err.response?.data?.detail || 'Nie udało się pobrać analizy zamówień.');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, isSchuco]);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  const applyFilters = event => {
    event.preventDefault();
    if (filters.startDate && filters.endDate && filters.startDate > filters.endDate) {
      setError('Data początkowa nie może być późniejsza od końcowej.');
      return;
    }
    setAppliedFilters({ ...filters });
  };

  const clearFilters = () => {
    const empty = { startDate: '', endDate: '', department: '' };
    setFilters(empty);
    setAppliedFilters(empty);
  };

  const quality = analytics?.data_quality;
  const hasQualityWarnings = quality && (
    quality.positions_without_initial_due_date > 0
    || quality.completed_without_delivery_date > 0
    || quality.unmatched_delivery_rows > 0
    || quality.partial_delivery_rows > 0
  );

  return (
    <div className="page-container oa-page">
      <div className="page-header oa-page-header">
        <div>
          <Link to="/management/order-analytics" className="oa-back"><ArrowLeft size={16} /> Analizy zamówień</Link>
          <h2 className="module-page-title"><TrendingUp aria-hidden="true" /> {supplierLabel} — analiza zamówień i dostaw</h2>
          <p>Terminowość pełnych dostaw, zmiany terminów i zachowania zakupowe.</p>
        </div>
        <button className="oa-refresh" onClick={loadAnalytics} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'oa-spin' : ''} /> Odśwież
        </button>
      </div>

      <div className="page-content">
        <form className="oa-filters" onSubmit={applyFilters}>
          <label>Od daty zamówienia<input type="date" value={filters.startDate} onChange={e => setFilters(current => ({ ...current, startDate: e.target.value }))} /></label>
          <label>Do daty zamówienia<input type="date" value={filters.endDate} onChange={e => setFilters(current => ({ ...current, endDate: e.target.value }))} /></label>
          {!isSchuco && <label>Dział<select value={filters.department} onChange={e => setFilters(current => ({ ...current, department: e.target.value }))}><option value="">Wszystkie</option><option value="PVC">PVC</option><option value="ALU">ALU</option></select></label>}
          <button type="submit" className="oa-primary" disabled={loading}>Zastosuj</button>
          <button type="button" className="oa-secondary" onClick={clearFilters} disabled={loading}><RotateCcw size={15} /> Wyczyść</button>
        </form>

        {error && <div className="oa-alert oa-alert--error"><AlertTriangle size={18} /> {String(error)}</div>}
        {loading && !analytics && <div className="oa-loading"><RefreshCw className="oa-spin" /> Obliczanie analizy…</div>}

        {analytics && (
          <>
            <div className="oa-scope">
              Analizowany okres: <strong>{formatDate(analytics.filters.available_start_date)}–{formatDate(analytics.filters.available_end_date)}</strong>
              {analytics.filters.department && <> · dział <strong>{analytics.filters.department}</strong></>}
            </div>

            <section className="oa-panel oa-order-impact">
              <div className="oa-panel__header"><div><span className="oa-eyebrow">Poziom całego zlecenia</span><h3>Terminowość i wpływ zmian na kompletne zlecenia</h3></div></div>
              <div className="oa-kpis oa-kpis--embedded">
                <KpiCard icon={PackageCheck} label="OTIF zamówień" value={`${formatNumber(analytics.order_otif.otif_rate)}%`} detail={`${formatInteger(analytics.order_otif.otif_orders)} z ${formatInteger(analytics.order_otif.eligible_orders)} kwalifikujących się · ${formatInteger(analytics.order_otif.non_otif_orders)} poza OTIF`} explanation={KPI_EXPLANATIONS.orderOtif} tone="positive" />
                <KpiCard icon={AlertTriangle} label="Zlecenia otwarte po terminie" value={`${formatNumber(analytics.order_fulfillment.overdue_open_orders_rate)}%`} detail={`${formatInteger(analytics.order_fulfillment.overdue_open_orders)} z ${formatInteger(analytics.order_fulfillment.eligible_orders)} · śr. ${formatNumber(analytics.order_fulfillment.average_overdue_open_days)} dni po terminie`} explanation={KPI_EXPLANATIONS.overdueOpenOrders} tone={analytics.order_fulfillment.overdue_open_orders ? 'negative' : 'positive'} />
                <KpiCard icon={Clock3} label="Faktyczne opóźnienie kompletacji" value={`${formatNumber(analytics.order_fulfillment.average_completion_delay_days)} dni`} detail={`mediana ${formatNumber(analytics.order_fulfillment.median_completion_delay_days)} · P90 ${formatInteger(analytics.order_fulfillment.p90_completion_delay_days)} · maks. ${formatInteger(analytics.order_fulfillment.maximum_completion_delay_days)} · ${formatInteger(analytics.order_fulfillment.late_completed_orders)} zleceń`} explanation={KPI_EXPLANATIONS.orderCompletionDelay} tone="negative" />
                <KpiCard icon={CalendarDays} label="Średni czas realizacji zlecenia" value={`${formatNumber(analytics.order_fulfillment.average_order_lead_time_days)} dni`} detail={`mediana ${formatNumber(analytics.order_fulfillment.median_order_lead_time_days)} dni · ${formatInteger(analytics.order_fulfillment.completed_orders)} zakończonych`} explanation={KPI_EXPLANATIONS.orderLeadTime} />
                <KpiCard icon={RefreshCw} label="Zlecenia ze zmianami" value={`${formatNumber(analytics.order_deadline_impact.changed_orders_rate)}%`} detail={`${formatInteger(analytics.order_deadline_impact.changed_orders)} z ${formatInteger(analytics.order_deadline_impact.total_orders)} zleceń`} explanation={KPI_EXPLANATIONS.changedOrders} />
                <KpiCard icon={AlertTriangle} label="Zlecenia wydłużone" value={`${formatNumber(analytics.order_deadline_impact.extended_orders_rate)}%`} detail={`${formatInteger(analytics.order_deadline_impact.extended_orders)} z ${formatInteger(analytics.order_deadline_impact.analyzable_orders)} z pełnymi terminami`} explanation={KPI_EXPLANATIONS.extendedOrders} tone="negative" />
                <KpiCard icon={Clock3} label="Średnie wydłużenie zlecenia" value={`${formatNumber(analytics.order_deadline_impact.average_extension_days)} dni`} detail={`mediana ${formatNumber(analytics.order_deadline_impact.median_extension_days)} dni · tylko wydłużone`} explanation={KPI_EXPLANATIONS.averageOrderExtension} tone="negative" />
                <KpiCard icon={CalendarDays} label="Maksymalne wydłużenie zlecenia" value={`${formatInteger(analytics.order_deadline_impact.maximum_extension_days)} dni`} detail={`średnio dla wszystkich: ${formatNumber(analytics.order_deadline_impact.average_extension_all_orders_days)} dni`} explanation={KPI_EXPLANATIONS.maximumOrderExtension} tone="negative" />
              </div>
              {(analytics.order_otif.not_yet_due_orders > 0 || analytics.order_otif.orders_without_initial_due_dates > 0) && <p className="oa-order-impact__note">OTIF poza próbą: {formatInteger(analytics.order_otif.not_yet_due_orders)} zleceń z terminem dzisiaj lub później · {formatInteger(analytics.order_otif.orders_without_initial_due_dates)} bez kompletu pierwotnych terminów.</p>}
              {analytics.order_deadline_impact.orders_without_complete_deadlines > 0 && <p className="oa-order-impact__note">Poza próbą: {formatInteger(analytics.order_deadline_impact.orders_without_complete_deadlines)} zleceń bez kompletu pierwotnych lub aktualnych terminów.</p>}
            </section>

            <section className="oa-panel oa-position-impact">
              <div className="oa-panel__header"><div><span className="oa-eyebrow">Poziom pozycji w zleceniu</span><h3>Terminowość i realizacja pojedynczych pozycji</h3></div></div>
              <div className="oa-kpis oa-kpis--embedded">
                <KpiCard icon={PackageCheck} label="Pozycje zamówień" value={formatInteger(analytics.kpis.total_positions)} detail={`${formatInteger(analytics.kpis.completed_positions)} zakończonych · ${formatInteger(analytics.kpis.open_positions)} otwartych`} explanation={KPI_EXPLANATIONS.positions} />
                <KpiCard icon={CheckCircle2} label="Dostarczone w terminie" value={`${formatNumber(analytics.kpis.on_time_rate)}%`} detail={`${formatInteger(analytics.kpis.on_time_positions)} z ${formatInteger(analytics.kpis.on_time_sample_size)} · dokładnie w dniu terminu: ${formatNumber(analytics.kpis.exact_due_date_rate)}% · wcześniej: ${formatInteger(analytics.kpis.early_positions)}`} explanation={KPI_EXPLANATIONS.onTime} tone="positive" />
                <KpiCard icon={Clock3} label="Średnie opóźnienie" value={`${formatNumber(analytics.kpis.average_delay_days)} dni`} detail={`mediana ${formatNumber(analytics.kpis.median_delay_days)} dni · tylko spóźnione`} explanation={KPI_EXPLANATIONS.averageDelay} tone="negative" />
                <KpiCard icon={AlertTriangle} label="Maksymalne opóźnienie" value={`${formatInteger(analytics.kpis.maximum_delay_days)} dni`} detail={`${formatInteger(analytics.kpis.delayed_positions)} zakończonych po terminie`} explanation={KPI_EXPLANATIONS.maximumDelay} tone="negative" />
                <KpiCard icon={RefreshCw} label="Zmiany terminów" value={formatInteger(analytics.kpis.total_change_events)} detail={`${formatInteger(analytics.kpis.changed_positions)} pozycji · śr. ${formatNumber(analytics.kpis.average_changes_per_position)}`} explanation={KPI_EXPLANATIONS.changes} />
                <KpiCard icon={CalendarDays} label="Średni czas realizacji" value={`${formatNumber(analytics.kpis.average_lead_time_days)} dni`} detail="od zamówienia do pełnej dostawy" explanation={KPI_EXPLANATIONS.leadTime} />
                <KpiCard icon={AlertTriangle} label="Otwarte po terminie" value={formatInteger(analytics.kpis.overdue_open_positions)} detail={`z ${formatInteger(analytics.kpis.open_positions)} otwartych pozycji`} explanation={KPI_EXPLANATIONS.overdueOpen} tone={analytics.kpis.overdue_open_positions ? 'negative' : 'positive'} />
                <KpiCard icon={PackageCheck} label="Pozostała ilość" value={formatNumber(analytics.kpis.remaining_quantity)} detail={`${formatNumber(analytics.kpis.delivered_quantity)} z ${formatNumber(analytics.kpis.ordered_quantity)} dostarczono`} explanation={KPI_EXPLANATIONS.remaining} />
              </div>
            </section>

            {hasQualityWarnings && (
              <section className="oa-alert oa-alert--warning">
                <AlertTriangle size={20} />
                <div><strong>Jakość danych wpływająca na analizę</strong><span>
                  Bez pierwotnego terminu: {formatInteger(quality.positions_without_initial_due_date)} · bez daty pełnej dostawy: {formatInteger(quality.completed_without_delivery_date)} · unmatched: {formatInteger(quality.unmatched_delivery_rows)} ({formatNumber(quality.unmatched_delivery_quantity)} szt.) · partial: {formatInteger(quality.partial_delivery_rows)} ({formatNumber(quality.partial_unallocated_quantity)} szt.)
                </span></div>
              </section>
            )}

            <div className="oa-chart-grid">
              <section className="oa-panel">
                <div className="oa-panel__header"><div><span className="oa-eyebrow">Rytm zakupów</span><h3>W które dni składamy zamówienia?</h3></div></div>
                <ResponsiveContainer width="100%" height={310}>
                  <BarChart data={analytics.weekday_orders} margin={{ top: 10, right: 15, left: -10, bottom: 15 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="weekday" angle={-20} textAnchor="end" height={55} />
                    <YAxis allowDecimals={false} />
                    <Tooltip formatter={(value, name) => [formatNumber(value), name === 'orders' ? 'Zamówienia' : 'Pozycje']} />
                    <Legend formatter={value => value === 'orders' ? 'Zamówienia' : 'Pozycje'} />
                    <Bar dataKey="orders" fill="#2f855a" radius={[5, 5, 0, 0]} />
                    <Bar dataKey="positions" fill="#9ac7a5" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </section>

              <section className="oa-panel">
                <div className="oa-panel__header"><div><span className="oa-eyebrow">Trend miesięczny</span><h3>Wolumen, terminowość i zmiany</h3></div></div>
                <ResponsiveContainer width="100%" height={310}>
                  <ComposedChart data={analytics.monthly_trend} margin={{ top: 10, right: 10, left: -10, bottom: 15 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" tickFormatter={formatMonth} />
                    <YAxis yAxisId="count" allowDecimals={false} />
                    <YAxis yAxisId="percent" orientation="right" domain={[0, 100]} unit="%" />
                    <Tooltip labelFormatter={formatMonth} formatter={(value, name) => [name === 'on_time_rate' ? `${formatNumber(value)}%` : formatNumber(value), name === 'positions' ? 'Pozycje' : name === 'change_events' ? 'Zmiany' : 'Terminowość']} />
                    <Legend formatter={value => value === 'positions' ? 'Pozycje' : value === 'change_events' ? 'Zmiany' : 'Terminowość %'} />
                    <Bar yAxisId="count" dataKey="positions" fill="#315b47" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="count" dataKey="change_events" fill="#d7a84b" radius={[4, 4, 0, 0]} />
                    <Line yAxisId="percent" type="monotone" dataKey="on_time_rate" stroke="#2563eb" strokeWidth={3} dot={{ r: 3 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </section>
            </div>

            {!isSchuco && analytics.department_breakdown.length > 0 && (
              <section className="oa-panel oa-departments">
                <div className="oa-panel__header"><div><span className="oa-eyebrow">Porównanie</span><h3>Wyniki według działu</h3></div></div>
                <div className="oa-department-grid">
                  {analytics.department_breakdown.map(item => <article key={item.department}><strong>{item.department}</strong><span>{formatInteger(item.positions)} pozycji</span><b>{formatNumber(item.on_time_rate)}% w terminie</b><small>{formatInteger(item.delayed_positions)} opóźnionych · {formatNumber(item.remaining_quantity)} pozostało</small></article>)}
                </div>
              </section>
            )}

            <AnalyticsTable title="Największe opóźnienia pełnych dostaw" rows={analytics.top_delays} mode="delay" />
            <AnalyticsTable title="Najczęściej zmieniane terminy" rows={analytics.most_changed_positions} mode="changes" />
            <OrderExtensionTable rows={analytics.top_order_extensions} />

            <details className="oa-definitions">
              <summary>Jak liczone są wskaźniki?</summary>
              <dl>{Object.entries(analytics.definitions).map(([key, value]) => <div key={key}><dt>{key.replaceAll('_', ' ')}</dt><dd>{value}</dd></div>)}</dl>
            </details>
          </>
        )}
      </div>
    </div>
  );
};

const WinkhausOrderAnalytics = () => <SupplierOrderAnalytics supplier="winkhaus" />;

export default WinkhausOrderAnalytics;
